<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import getNetwatchUser from '@/lib/getNetwatchUser'

const props = defineProps<{
  apiHost?: string
  clientId?: string
}>()

interface HostGroup {
  id: string | number
  name: string
  description?: string
}

interface ReportConfiguration {
  id?: number
  configuration_name: string
  os_type_preference: string
  time_period_preference: string
  frequency: string
  scheduled_time: string
  scheduled_day_of_week?: number
  scheduled_day_of_month?: number
  email_addresses?: string
  host_group_ids?: Array<{ id: string; name: string }> | string[] | number[]
  is_active: boolean
  last_sent_at?: string
  next_scheduled_at?: string
  report_type: {
    id: number
    name: string
    display_name: string
    description: string
  }
  created_at?: string
  updated_at?: string
}

const configurations = ref<ReportConfiguration[]>([])
const originalConfigurations = ref<ReportConfiguration[]>([])
const isLoading = ref(false)
const isSending = ref<number | null>(null)
const error = ref<string | null>(null)
const successMessage = ref<string | null>(null)
const hasChanges = ref(false)
const showConfirmModal = ref(false)
const confirmAction = ref<() => void>(() => {})
const confirmMessage = ref('')
const confirmTitle = ref('Confirm Action')
const confirmButtonClass = ref('confirm-button')
const expandedConfigId = ref<number | null>(null)
const showAddModal = ref(false)
const editingConfig = ref<ReportConfiguration | null>(null)
const isSaving = ref(false)

const osTypeOptions = [
  { value: 'windows', label: '🪟 Windows' },
  { value: 'linux', label: '🐧 Linux' },
  { value: 'ip_devices', label: '📡 IP Devices' },
  { value: 'all', label: '🌐 All Systems' },
]

const timePeriodOptions = [
  { value: 'last_30d', label: '📅 Last 30 Days' },
  { value: 'last_90d', label: '📊 Last 90 Days' },
  { value: 'last_6m', label: '📈 Last 6 Months' },
  { value: 'last_1y', label: '🗓️ Last Year' },
  { value: 'all_time', label: '♾️ All Time' },
]

const frequencyOptions = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half-yearly', label: 'Half-yearly' },
  { value: 'yearly', label: 'Yearly' },
]

const dayOfWeekOptions = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]

// Host groups data (loaded from API)
const hostGroups = ref<HostGroup[]>([])
const isLoadingHostGroups = ref(false)

// Host group dropdown state
const hostGroupDropdownOpen = ref(false)
const hostGroupSearchQuery = ref('')

// Filtered host groups based on search query
const filteredHostGroups = computed(() => {
  if (!hostGroupSearchQuery.value.trim()) {
    return hostGroups.value
  }
  const query = hostGroupSearchQuery.value.toLowerCase()
  return hostGroups.value.filter(group =>
    group.name.toLowerCase().includes(query) ||
    (group.description && group.description.toLowerCase().includes(query))
  )
})

// Check if all filtered host groups are selected
const areAllFilteredHostGroupsSelected = computed(() => {
  if (filteredHostGroups.value.length === 0) return false
  return filteredHostGroups.value.every(group => isHostGroupSelected(group.id))
})

// Toggle select all filtered host groups
function toggleSelectAllFiltered() {
  if (!formData.value.host_group_ids) {
    formData.value.host_group_ids = []
  }
  
  if (areAllFilteredHostGroupsSelected.value) {
    // Deselect all filtered groups
    const currentIds = (formData.value.host_group_ids as any[]).map((item: any) => {
      if (typeof item === 'object' && item.id) {
        return { id: String(item.id), name: item.name || '' }
      }
      const foundGroup = hostGroups.value.find(g => String(g.id) === String(item))
      return { id: String(item), name: foundGroup?.name || '' }
    })
    
    // Remove all filtered groups
    filteredHostGroups.value.forEach(group => {
      const index = currentIds.findIndex(item => String(item.id) === String(group.id))
      if (index > -1) {
        currentIds.splice(index, 1)
      }
    })
    
    formData.value.host_group_ids = currentIds.length > 0 ? currentIds : []
  } else {
    // Select all filtered groups
    const currentIds = (formData.value.host_group_ids as any[]).map((item: any) => {
      if (typeof item === 'object' && item.id) {
        return { id: String(item.id), name: item.name || '' }
      }
      const foundGroup = hostGroups.value.find(g => String(g.id) === String(item))
      return { id: String(item), name: foundGroup?.name || '' }
    })
    
    // Add all filtered groups that aren't already selected
    filteredHostGroups.value.forEach(group => {
      const exists = currentIds.some(item => String(item.id) === String(group.id))
      if (!exists) {
        currentIds.push({ id: String(group.id), name: group.name || '' })
      }
    })
    
    formData.value.host_group_ids = currentIds
  }
}

// Check if host group is selected
function isHostGroupSelected(groupId: string | number): boolean {
  if (!formData.value.host_group_ids || formData.value.host_group_ids.length === 0) {
    return false
  }
  // Handle both array of objects and array of IDs
  return formData.value.host_group_ids.some((hg: any) => {
    if (typeof hg === 'object' && hg.id) {
      return String(hg.id) === String(groupId)
    }
    return String(hg) === String(groupId)
  })
}

// Toggle host group selection
function toggleHostGroup(groupId: string | number) {
  if (!formData.value.host_group_ids) {
    formData.value.host_group_ids = []
  }
  
  // Find the host group to get its name
  const hostGroup = hostGroups.value.find(group => String(group.id) === String(groupId))
  const hostGroupName = hostGroup?.name || ''
  
  // Convert to array of objects format
  const currentIds = (formData.value.host_group_ids as any[]).map((item: any) => {
    if (typeof item === 'object' && item.id) {
      return { id: String(item.id), name: item.name || '' }
    }
    // If it's just an ID, find the name
    const foundGroup = hostGroups.value.find(g => String(g.id) === String(item))
    return { id: String(item), name: foundGroup?.name || '' }
  })
  
  const index = currentIds.findIndex(item => String(item.id) === String(groupId))
  if (index > -1) {
    currentIds.splice(index, 1)
  }
  else {
    currentIds.push({ id: String(groupId), name: hostGroupName })
  }
  
  formData.value.host_group_ids = currentIds
}

// Get selected host group names
function getSelectedHostGroupNames(): string {
  if (!formData.value.host_group_ids || formData.value.host_group_ids.length === 0) {
    return 'No host groups selected'
  }
  
  // Handle both array of objects and array of IDs
  const selected = (formData.value.host_group_ids as any[]).map((hg: any) => {
    if (typeof hg === 'object' && hg.name) {
      return hg.name
    }
    // If it's just an ID, find the name
    const group = hostGroups.value.find(g => String(g.id) === String(hg))
    return group?.name || String(hg)
  }).filter(Boolean)
  
  if (selected.length <= 2) {
    return selected.join(', ')
  }
  return `${selected.slice(0, 2).join(', ')} and ${selected.length - 2} more`
}

// Close dropdown when clicking outside
function closeHostGroupDropdown() {
  hostGroupDropdownOpen.value = false
  hostGroupSearchQuery.value = ''
}

// Form data for new/editing configurations
const formData = ref<ReportConfiguration>({
  configuration_name: '',
  os_type_preference: 'all',
  time_period_preference: 'last_30d',
  frequency: 'monthly',
  scheduled_time: '09:00',
  scheduled_day_of_week: 1,
  scheduled_day_of_month: 1,
  email_addresses: '',
  host_group_ids: [],
  is_active: true,
  report_type: {
    id: 1,
    name: 'monthly_sla',
    display_name: 'Monthly SLA Report',
    description: 'Comprehensive monthly service level agreement report',
  },
})

// Load host groups
async function loadHostGroups() {
  if (!props.apiHost || !props.clientId) {
    return
  }

  isLoadingHostGroups.value = true

  try {
    const username = getNetwatchUser()
    const baseUrl = props.apiHost.replace('/api/chat', '/api')
    const response = await fetch(`${baseUrl}/reports/configurations/${props.clientId}/host-groups`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-client-id': props.clientId,
        'x-username': username,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to load host groups: ${response.status}`)
    }

    const data = await response.json()
    if (data.success) {
      // Convert API response to HostGroup format
      hostGroups.value = (data.data || []).map((hg: any) => ({
        id: hg.id,
        name: hg.name,
        description: undefined, // API doesn't return description for now
      }))
    }
    else {
      throw new Error(data.error || 'Failed to load host groups')
    }
  }
  catch (err) {
    console.error('Error loading host groups:', err)
    // Don't show error to user, just log it - host groups are optional
  }
  finally {
    isLoadingHostGroups.value = false
  }
}

// Load configurations
async function loadConfigurations() {
  if (!props.apiHost || !props.clientId) {
    error.value = 'API configuration is missing'
    return
  }

  isLoading.value = true
  error.value = null

  try {
    const username = getNetwatchUser()
    const baseUrl = props.apiHost.replace('/api/chat', '/api')
    const response = await fetch(`${baseUrl}/reports/configurations/${props.clientId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-client-id': props.clientId,
        'x-username': username,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to load configurations: ${response.status}`)
    }

    const data = await response.json()
    if (data.success) {
      configurations.value = data.data || []
      originalConfigurations.value = JSON.parse(JSON.stringify(data.data || []))
      hasChanges.value = false
    }
    else {
      throw new Error(data.error || 'Failed to load configurations')
    }
  }
  catch (err) {
    error.value = err instanceof Error ? err.message : 'An error occurred'
    console.error('Error loading configurations:', err)
  }
  finally {
    isLoading.value = false
  }
}

// Unsubscribe from configuration
async function unsubscribeFromConfiguration(configId: number, configName: string) {
  if (!props.apiHost || !props.clientId)
    return

  // Show confirmation modal
  confirmTitle.value = 'Unsubscribe from Report'
  confirmMessage.value = `Are you sure you want to unsubscribe from "${configName}"? This will stop all future automated reports for this configuration.`
  confirmAction.value = () => performUnsubscribe(configId)
  confirmButtonClass.value = 'confirm-button-danger'
  showConfirmModal.value = true
}

// Actually perform the unsubscribe action
async function performUnsubscribe(configId: number) {
  try {
    const username = getNetwatchUser()
    const baseUrl = props.apiHost!.replace('/api/chat', '/api')
    const response = await fetch(`${baseUrl}/reports/configurations/${configId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'x-client-id': props.clientId!,
        'x-username': username,
      },
    })

    if (!response.ok) {
      throw new Error('Failed to unsubscribe from configuration')
    }

    await loadConfigurations()
    successMessage.value = 'Successfully unsubscribed from report configuration'
    setTimeout(() => {
      successMessage.value = null
    }, 3000)
  }
  catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to unsubscribe from configuration'
  }
}

// Send report now
async function sendNow(configId: number) {
  if (!props.apiHost || !props.clientId)
    return

  // Show confirmation modal
  confirmTitle.value = 'Send Report Now'
  confirmMessage.value = 'Are you sure you want to send this report now? The report will be delivered immediately to all configured email addresses.'
  confirmAction.value = () => performSendNow(configId)
  confirmButtonClass.value = 'confirm-button-success'
  showConfirmModal.value = true
}

// Actually perform the send now action
async function performSendNow(configId: number) {
  try {
    isSending.value = configId
    const username = getNetwatchUser()
    const baseUrl = props.apiHost!.replace('/api/chat', '/api')
    const response = await fetch(`${baseUrl}/reports/configurations/${configId}/send-now`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-client-id': props.clientId!,
        'x-username': username,
      },
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send report')
    }

    await loadConfigurations() // Reload to update last_sent_at
    successMessage.value = data.message || 'Report sent successfully'
    setTimeout(() => {
      successMessage.value = null
    }, 3000)
  }
  catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to send report'
  }
  finally {
    isSending.value = null
  }
}

// Close confirmation modal
function closeConfirmModal() {
  showConfirmModal.value = false
  confirmAction.value = () => {}
  confirmMessage.value = ''
  confirmTitle.value = 'Confirm Action'
  confirmButtonClass.value = 'confirm-button'
}

// Execute confirmed action
function executeConfirmedAction() {
  confirmAction.value()
  closeConfirmModal()
}

// Toggle expanded view
function toggleExpanded(configId: number) {
  if (expandedConfigId.value === configId) {
    expandedConfigId.value = null
  }
  else {
    expandedConfigId.value = configId
  }
}

// Create or update configuration
async function saveConfiguration(config: ReportConfiguration) {
  if (!props.apiHost || !props.clientId)
    return

  const username = getNetwatchUser()
  const baseUrl = props.apiHost.replace('/api/chat', '/api')
  const url = config.id
    ? `${baseUrl}/reports/configurations/${config.id}`
    : `${baseUrl}/reports/configurations`

  const method = config.id ? 'PUT' : 'POST'

  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'x-client-id': props.clientId!,
      'x-username': username,
    },
    body: JSON.stringify({
      ...config,
      client_id: props.clientId,
      report_type_id: 1, // SLA report type
    }),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || 'Failed to save configuration')
  }

  return await response.json()
}

// Handle form submission
async function handleFormSubmit() {
  if (!validateForm())
    return

  try {
    isSaving.value = true
    error.value = null

    await saveConfiguration(formData.value)
    await loadConfigurations()

    closeModal()
    successMessage.value = formData.value.id ? 'Configuration updated successfully' : 'Configuration created successfully'
    setTimeout(() => {
      successMessage.value = null
    }, 3000)
  }
  catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to save configuration'
  }
  finally {
    isSaving.value = false
  }
}

// Validate form
function validateForm() {
  if (!formData.value.configuration_name.trim()) {
    error.value = 'Configuration name is required'
    return false
  }

  if (formData.value.email_addresses && !validateEmails(formData.value.email_addresses)) {
    error.value = 'Please enter valid email addresses separated by commas'
    return false
  }

  return true
}

// Validate email addresses
function validateEmails(emails: string): boolean {
  if (!emails.trim())
    return true // Email is optional

  const emailList = emails.split(',').map(email => email.trim())
  const emailRegex = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/

  return emailList.every((email) => {
    if (!email)
      return false
    return emailRegex.test(email)
  })
}

// Open add modal
function openAddModal() {
  formData.value = {
    configuration_name: '',
    os_type_preference: 'all',
    time_period_preference: 'last_30d',
    frequency: 'monthly',
    scheduled_time: '09:00',
    scheduled_day_of_week: 1,
    scheduled_day_of_month: 1,
    email_addresses: '',
    host_group_ids: [],
    is_active: true,
    report_type: {
      id: 1,
      name: 'monthly_sla',
      display_name: 'Monthly SLA Report',
      description: 'Comprehensive monthly service level agreement report',
    },
  }
  showAddModal.value = true
  editingConfig.value = null
  error.value = null
  closeHostGroupDropdown()
}

// Open edit modal
function openEditModal(config: ReportConfiguration) {
  // Ensure host_group_ids is in the correct format (array of objects with id and name)
  let formattedHostGroupIds: Array<{ id: string; name: string }> = []
  if (config.host_group_ids && Array.isArray(config.host_group_ids) && config.host_group_ids.length > 0) {
    formattedHostGroupIds = config.host_group_ids.map((hg: any) => {
      if (typeof hg === 'object' && hg.id) {
        return { id: String(hg.id), name: hg.name || '' }
      }
      // If it's just an ID, find the name from hostGroups
      const group = hostGroups.value.find(g => String(g.id) === String(hg))
      return { id: String(hg), name: group?.name || '' }
    })
  }
  
  formData.value = {
    ...config,
    host_group_ids: formattedHostGroupIds,
  }
  showAddModal.value = true
  editingConfig.value = config
  error.value = null
  closeHostGroupDropdown()
}

// Close modal
function closeModal() {
  showAddModal.value = false
  editingConfig.value = null
  error.value = null
  closeHostGroupDropdown()
}

// Format date
function formatDate(dateString?: string) {
  if (!dateString)
    return 'Never'
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Format date short
function formatDateShort(dateString?: string) {
  if (!dateString)
    return 'Never'
  const date = new Date(dateString)
  const now = new Date()
  const diffTime = Math.abs(now.getTime() - date.getTime())
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return 'Today'
  }
  else if (diffDays === 1) {
    return date > now ? 'Tomorrow' : 'Yesterday'
  }
  else if (diffDays < 7) {
    return `${diffDays}d ${date > now ? 'from now' : 'ago'}`
  }
  else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
}

// Format frequency
function formatFrequency(frequency: string, dayOfWeek?: number, dayOfMonth?: number) {
  switch (frequency) {
    case 'daily':
      return 'Daily'
    case 'weekly': {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      return `Weekly (${days[dayOfWeek || 1]})`
    }
    case 'monthly':
      return `Monthly (Day ${dayOfMonth || 1})`
    case 'quarterly':
      return 'Quarterly'
    case 'half-yearly':
      return 'Half-Yearly'
    case 'yearly':
      return 'Yearly'
    default:
      return frequency
  }
}

// Format OS type
function formatOsType(osType: string) {
  const option = osTypeOptions.find(opt => opt.value === osType)
  return option ? option.label : osType
}

// Format time period
function formatTimePeriod(timePeriod: string) {
  const option = timePeriodOptions.find(opt => opt.value === timePeriod)
  return option ? option.label : timePeriod
}

// Format frequency short
function formatFrequencyShort(frequency: string, dayOfWeek?: number, dayOfMonth?: number) {
  switch (frequency) {
    case 'daily':
      return 'Daily'
    case 'weekly': {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      return `${days[dayOfWeek || 1]}`
    }
    case 'monthly':
      return `Day ${dayOfMonth || 1}`
    case 'quarterly':
      return 'Quarterly'
    case 'half-yearly':
      return 'Half-Yr'
    case 'yearly':
      return 'Yearly'
    default:
      return frequency
  }
}

// Get OS type icon
function formatOsTypeIcon(osType: string) {
  switch (osType) {
    case 'windows':
      return '🪟'
    case 'linux':
      return '🐧'
    case 'ip_devices':
      return '📡'
    case 'all':
      return '🌐'
    default:
      return '💻'
  }
}

// Get OS type short name
function formatOsTypeShort(osType: string) {
  switch (osType) {
    case 'windows':
      return 'Win'
    case 'linux':
      return 'Linux'
    case 'ip_devices':
      return 'IP'
    case 'all':
      return 'All'
    default:
      return osType
  }
}

onMounted(() => {
  loadConfigurations()
  loadHostGroups()
})
</script>

<template>
  <div class="reports-container">
    <div class="reports-content">
      <div class="reports-header">
        <h2 class="reports-title">
          ⚙️ Report Configurations
        </h2>
        <p class="reports-subtitle">
          Manage your automated SLA report configurations with custom schedules and settings:
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="reports-loading">
        <div class="loading-spinner" />
        <p>Loading configurations...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="reports-error">
        <div class="error-icon">
          ⚠️
        </div>
        <p>{{ error }}</p>
        <button class="retry-button" @click="loadConfigurations">
          Try Again
        </button>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" class="reports-success">
        <div class="success-icon">
          ✅
        </div>
        <p>{{ successMessage }}</p>
      </div>

      <!-- Configurations List -->
      <div v-else class="configurations-container">
        <!-- Add Configuration Button -->
        <div class="add-config-header">
          <button class="add-config-button" @click="openAddModal">
            <span class="add-icon">+</span>
            Add New Configuration
          </button>
        </div>

        <!-- Configurations List -->
        <div v-if="configurations.length > 0" class="configurations-list">
          <div v-for="config in configurations" :key="config.id" class="config-wrapper">
            <div class="config-item" :class="{ expanded: expandedConfigId === config.id }">
              <div class="config-main" @click="toggleExpanded(config.id!)">
                <div class="config-title-row">
                  <h3 class="config-name">
                    {{ config.configuration_name }}
                  </h3>
                  <span v-if="config.is_active" class="status-active">●</span>
                  <span v-else class="status-inactive">●</span>
                  <span class="expand-icon" :class="{ expanded: expandedConfigId === config.id }">
                    ▶
                  </span>
                </div>

                <div v-if="expandedConfigId !== config.id" class="config-details-grid">
                  <div class="detail-item">
                    <span class="detail-icon">{{ formatOsTypeIcon(config.os_type_preference) }}</span>
                    <span class="detail-text">{{ formatOsTypeShort(config.os_type_preference) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-icon">📅</span>
                    <span class="detail-text">{{ formatFrequencyShort(config.frequency, config.scheduled_day_of_week, config.scheduled_day_of_month) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-icon">🕐</span>
                    <span class="detail-text">{{ config.scheduled_time }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-icon">📧</span>
                    <span class="detail-text">{{ config.email_addresses ? config.email_addresses.split(',').length : 0 }}</span>
                  </div>
                </div>

                <div v-if="expandedConfigId !== config.id" class="config-schedule-info">
                  <span class="schedule-text">
                    Last: {{ formatDateShort(config.last_sent_at) }} • Next: {{ formatDateShort(config.next_scheduled_at) }}
                  </span>
                </div>
              </div>

              <div class="config-actions" @click.stop>
                <button
                  class="action-btn-labeled send-btn"
                  :class="{ sending: isSending === config.id }"
                  :disabled="isSending === config.id || config.report_type.name !== 'monthly_sla'"
                  :title="config.report_type.name !== 'monthly_sla' ? 'Only SLA reports are currently available' : 'Send report now'"
                  @click="sendNow(config.id!)"
                >
                  <span v-if="!isSending || isSending !== config.id" class="btn-icon">📤</span>
                  <div v-else class="button-spinner" />
                  <span v-if="!isSending || isSending !== config.id" class="btn-label">Send</span>
                </button>
                <button
                  class="action-btn-labeled edit-btn"
                  title="Edit configuration"
                  @click="openEditModal(config)"
                >
                  <span class="btn-icon">✏️</span>
                  <span class="btn-label">Edit</span>
                </button>
                <button
                  class="action-btn-labeled unsub-btn"
                  title="Unsubscribe from this configuration"
                  @click="unsubscribeFromConfiguration(config.id!, config.configuration_name)"
                >
                  <span class="btn-icon">✕</span>
                  <span class="btn-label">Remove</span>
                </button>
              </div>
            </div>

            <!-- Expanded Details -->
            <div v-if="expandedConfigId === config.id" class="config-expanded">
              <div class="expanded-section">
                <h4 class="expanded-title">
                  📊 Report Details
                </h4>
                <div class="expanded-content">
                  <div class="expanded-row">
                    <span class="expanded-label">Report Type:</span>
                    <span class="expanded-value">{{ config.report_type.display_name }}</span>
                  </div>
                  <div class="expanded-row">
                    <span class="expanded-label">Description:</span>
                    <span class="expanded-value">{{ config.report_type.description }}</span>
                  </div>
                  <div class="expanded-row">
                    <span class="expanded-label">OS Filter:</span>
                    <span class="expanded-value">{{ formatOsType(config.os_type_preference) }}</span>
                  </div>
                  <div class="expanded-row">
                    <span class="expanded-label">Report Period:</span>
                    <span class="expanded-value">{{ formatTimePeriod(config.time_period_preference) }}</span>
                  </div>
                </div>
              </div>

              <div class="expanded-section">
                <h4 class="expanded-title">
                  ⏰ Schedule Information
                </h4>
                <div class="expanded-content">
                  <div class="expanded-row">
                    <span class="expanded-label">Frequency:</span>
                    <span class="expanded-value">{{ formatFrequency(config.frequency, config.scheduled_day_of_week, config.scheduled_day_of_month) }}</span>
                  </div>
                  <div class="expanded-row">
                    <span class="expanded-label">Time:</span>
                    <span class="expanded-value">{{ config.scheduled_time }}</span>
                  </div>
                  <div class="expanded-row">
                    <span class="expanded-label">Last Sent:</span>
                    <span class="expanded-value">{{ formatDate(config.last_sent_at) }}</span>
                  </div>
                  <div class="expanded-row">
                    <span class="expanded-label">Next Scheduled:</span>
                    <span class="expanded-value">{{ formatDate(config.next_scheduled_at) }}</span>
                  </div>
                </div>
              </div>

              <div v-if="config.email_addresses" class="expanded-section">
                <h4 class="expanded-title">
                  📧 Email Recipients
                </h4>
                <div class="expanded-content">
                  <div class="email-list">
                    <span v-for="email in config.email_addresses.split(',').map(e => e.trim())" :key="email" class="email-chip">
                      {{ email }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="reports-empty">
          <div class="empty-icon">
            ⚙️
          </div>
          <p>
            No report configurations found
          </p>
          <button class="add-first-config-button" @click="openAddModal">
            Create Your First Configuration
          </button>
        </div>
      </div>

      <!-- Add/Edit Modal -->
      <div v-if="showAddModal" class="modal-overlay" @click="closeModal; closeHostGroupDropdown()">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="modal-title">
              {{ editingConfig ? 'Edit Configuration' : 'Add Configuration' }}
            </h3>
            <button class="modal-close" @click="closeModal">
              ×
            </button>
          </div>

          <form class="modal-form" @submit.prevent="handleFormSubmit">
            <!-- Configuration Name -->
            <div class="form-group">
              <label class="form-label">Configuration Name</label>
              <input
                v-model="formData.configuration_name"
                type="text"
                class="form-input"
                placeholder="e.g., Windows Servers Daily Report"
                required
              >
            </div>

            <!-- OS Type and Frequency -->
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">OS Type</label>
                <select v-model="formData.os_type_preference" class="form-select">
                  <option v-for="option in osTypeOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label">Report Period</label>
                <select v-model="formData.time_period_preference" class="form-select">
                  <option v-for="option in timePeriodOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label">Frequency</label>
                <select v-model="formData.frequency" class="form-select">
                  <option v-for="option in frequencyOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Host Groups -->
            <div class="form-group">
              <label class="form-label">Host Groups</label>
              <div class="host-group-dropdown-wrapper">
                <button
                  type="button"
                  class="host-group-dropdown-trigger"
                  :class="{ 'dropdown-open': hostGroupDropdownOpen }"
                  @click="hostGroupDropdownOpen = !hostGroupDropdownOpen"
                >
                  <span class="dropdown-trigger-text">
                    {{ getSelectedHostGroupNames() }}
                  </span>
                  <span class="dropdown-arrow" :class="{ 'arrow-up': hostGroupDropdownOpen }">
                    ▼
                  </span>
                </button>
                <div v-if="hostGroupDropdownOpen" class="host-group-dropdown" @click.stop>
                  <div class="dropdown-search">
                    <input
                      v-model="hostGroupSearchQuery"
                      type="text"
                      class="dropdown-search-input"
                      placeholder="Search host groups..."
                      @click.stop
                    >
                  </div>
                  <div v-if="filteredHostGroups.length > 0" class="dropdown-select-all">
                    <button
                      type="button"
                      class="select-all-button"
                      @click.stop="toggleSelectAllFiltered"
                    >
                      <input
                        type="checkbox"
                        :checked="areAllFilteredHostGroupsSelected"
                        class="select-all-checkbox"
                        @click.stop="toggleSelectAllFiltered"
                      >
                      <span class="select-all-text">
                        {{ areAllFilteredHostGroupsSelected ? 'Deselect All' : 'Select All' }}
                      </span>
                    </button>
                  </div>
                  <div class="dropdown-options">
                    <div
                      v-for="group in filteredHostGroups"
                      :key="group.id"
                      class="dropdown-option"
                      :class="{ 'option-selected': isHostGroupSelected(group.id) }"
                      @click="toggleHostGroup(group.id)"
                    >
                      <input
                        type="checkbox"
                        :checked="isHostGroupSelected(group.id)"
                        class="option-checkbox"
                        @click.stop="toggleHostGroup(group.id)"
                      >
                      <div class="option-content">
                        <span class="option-name">{{ group.name }}</span>
                        <span v-if="group.description" class="option-description">{{ group.description }}</span>
                      </div>
                    </div>
                    <div v-if="filteredHostGroups.length === 0" class="dropdown-no-results">
                      No host groups found
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="formData.host_group_ids && formData.host_group_ids.length > 0" class="selected-groups-chips">
                <span
                  v-for="hg in formData.host_group_ids"
                  :key="typeof hg === 'object' ? hg.id : hg"
                  class="group-chip"
                >
                  {{ typeof hg === 'object' && hg.name ? hg.name : (hostGroups.find(g => String(g.id) === String(hg))?.name || String(hg)) }}
                  <button
                    type="button"
                    class="chip-remove"
                    @click="toggleHostGroup(typeof hg === 'object' ? hg.id : hg)"
                  >
                    ×
                  </button>
                </span>
              </div>
            </div>

            <!-- Scheduling -->
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Scheduled Time</label>
                <input
                  v-model="formData.scheduled_time"
                  type="time"
                  class="form-input"
                  required
                >
              </div>

              <div v-if="formData.frequency === 'weekly'" class="form-group">
                <label class="form-label">Day of Week</label>
                <select v-model="formData.scheduled_day_of_week" class="form-select">
                  <option v-for="option in dayOfWeekOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <div v-if="formData.frequency === 'monthly'" class="form-group">
                <label class="form-label">Day of Month</label>
                <input
                  v-model.number="formData.scheduled_day_of_month"
                  type="number"
                  min="1"
                  max="31"
                  class="form-input"
                >
              </div>
            </div>

            <!-- Email Recipients -->
            <div class="form-group">
              <label class="form-label">Email Recipients (comma-separated)</label>
              <textarea
                v-model="formData.email_addresses"
                class="form-textarea"
                placeholder="user1@example.com, user2@example.com"
                rows="3"
              />
            </div>

            <!-- Active Status -->
            <div class="form-group">
              <label class="checkbox-container">
                <input v-model="formData.is_active" type="checkbox" class="form-checkbox">
                Configuration is active
              </label>
            </div>

            <!-- Error Display -->
            <div v-if="error" class="form-error">
              {{ error }}
            </div>

            <!-- Form Actions -->
            <div class="form-actions">
              <button type="button" class="cancel-button" @click="closeModal">
                Cancel
              </button>
              <button type="submit" class="save-button" :disabled="isSaving">
                <div v-if="isSaving" class="button-spinner" />
                {{ isSaving ? 'Saving...' : (editingConfig ? 'Update' : 'Create') }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Confirmation Modal -->
      <div v-if="showConfirmModal" class="modal-overlay" @click="closeConfirmModal">
        <div class="modal-content confirmation-modal" @click.stop>
          <div class="modal-header">
            <h3 class="modal-title">
              {{ confirmTitle }}
            </h3>
            <button class="modal-close" @click="closeConfirmModal">
              ×
            </button>
          </div>

          <div class="modal-body">
            <div class="confirmation-content">
              <div class="confirmation-icon">
                ⚠️
              </div>
              <p class="confirmation-message">
                {{ confirmMessage }}
              </p>
            </div>

            <div class="confirmation-actions">
              <button type="button" class="cancel-button" @click="closeConfirmModal">
                Cancel
              </button>
              <button type="button" :class="confirmButtonClass" @click="executeConfirmedAction">
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Information -->
      <div v-if="!showConfirmModal" class="reports-footer">
        <div class="footer-info">
          <p>
            <strong>⚙️ Configurations:</strong> Create multiple report configurations with different schedules and filters
          </p>
          <p>
            <strong>📧 Send Now:</strong> Manually trigger any configuration to send reports immediately
          </p>
          <p>
            <strong>🕒 Scheduling:</strong> Each configuration runs automatically based on your specified schedule
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.reports-container {
  @apply flex flex-col h-full bg-white;
}

.reports-content {
  @apply flex-1 p-4 overflow-y-auto;
}

.reports-header {
  @apply mb-6 text-center;
}

.reports-title {
  @apply text-xl font-bold text-gray-800 mb-2;
}

.reports-subtitle {
  @apply text-sm text-gray-600;
}

.reports-loading {
  @apply flex flex-col items-center justify-center py-8 text-gray-600;
}

.loading-spinner {
  @apply w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3;
}

.reports-error {
  @apply flex flex-col items-center justify-center py-8 text-red-600;
}

.error-icon {
  @apply text-2xl mb-2;
}

.retry-button {
  @apply mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors;
}

.reports-success {
  @apply flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 rounded-lg text-green-700;
}

.success-icon {
  @apply text-lg;
}

.configurations-container {
  @apply space-y-4;
}

.add-config-header {
  @apply flex justify-center mb-6;
}

.add-config-button {
  @apply flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm;
}

.add-icon {
  @apply text-lg font-bold;
}

.add-first-config-button {
  @apply mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm;
}

.configurations-list {
  @apply space-y-2;
}

.config-wrapper {
  @apply space-y-2;
}

.config-item {
  @apply bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-all flex items-center justify-between gap-3;
}

.config-item.expanded {
  @apply shadow-md border-blue-300;
}

.config-main {
  @apply flex-1 min-w-0 cursor-pointer;
}

.config-title-row {
  @apply flex items-center gap-2 mb-2;
}

.config-name {
  @apply text-sm font-semibold text-gray-800 truncate flex-1;
}

.status-active {
  @apply text-green-500 text-xs;
}

.status-inactive {
  @apply text-gray-400 text-xs;
}

.expand-icon {
  @apply text-gray-400 text-xs transition-transform duration-200;
}

.expand-icon.expanded {
  @apply rotate-90;
}

.config-details-grid {
  @apply flex flex-wrap gap-3 mb-2;
}

.detail-item {
  @apply flex items-center gap-1 text-xs;
}

.detail-icon {
  @apply text-xs;
}

.detail-text {
  @apply text-gray-600;
}

.config-schedule-info {
  @apply text-xs text-gray-500;
}

.schedule-text {
  @apply text-xs;
}

.config-actions {
  @apply flex items-center gap-2 flex-shrink-0;
}

.action-btn {
  @apply w-8 h-8 rounded-lg flex items-center justify-center transition-colors text-sm;
}

.action-btn-labeled {
  @apply flex items-center gap-1 px-2 py-1 rounded-lg transition-colors text-xs font-medium;
}

.btn-icon {
  @apply text-sm;
}

.btn-label {
  @apply text-xs;
}

.send-btn {
  @apply bg-green-50 hover:bg-green-100 text-green-600;
}

.send-btn.sending,
.send-btn:disabled {
  @apply bg-gray-100 text-gray-400 cursor-not-allowed;
}

.edit-btn {
  @apply bg-blue-50 hover:bg-blue-100 text-blue-600;
}

.unsub-btn {
  @apply bg-red-50 hover:bg-red-100 text-red-600;
}

/* Expanded View Styles */
.config-expanded {
  @apply bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4 animate-in;
}

.expanded-section {
  @apply space-y-2;
}

.expanded-title {
  @apply text-sm font-semibold text-gray-700 mb-2;
}

.expanded-content {
  @apply space-y-2 pl-6;
}

.expanded-row {
  @apply flex items-start gap-2 text-xs;
}

.expanded-label {
  @apply text-gray-500 min-w-[100px];
}

.expanded-value {
  @apply text-gray-700 flex-1;
}

.email-list {
  @apply flex flex-wrap gap-2;
}

.email-chip {
  @apply px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-600;
}

@keyframes animate-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-in {
  animation: animate-in 0.2s ease-out;
}

.reports-empty {
  @apply flex flex-col items-center justify-center py-12 text-gray-500;
}

.empty-icon {
  @apply text-4xl mb-3;
}

.empty-subtitle {
  @apply text-xs text-gray-400 mt-2;
}

.reports-footer {
  @apply mt-6 pt-4 border-t border-gray-200;
}

.footer-info {
  @apply space-y-2 text-xs text-gray-500;
}

.footer-info p {
  @apply flex items-start gap-1;
}

.button-spinner {
  @apply w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin;
}

/* Modal Styles */
.modal-overlay {
  @apply fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50;
}

.modal-content {
  @apply bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto;
}

.modal-header {
  @apply flex items-center justify-between p-6 border-b border-gray-200;
}

.modal-title {
  @apply text-xl font-semibold text-gray-900;
}

.modal-close {
  @apply text-gray-400 hover:text-gray-600 text-2xl font-bold;
}

/* Form Styles */
.modal-form {
  @apply p-6 space-y-4;
}

.form-group {
  @apply space-y-1;
}

.form-row {
  @apply grid grid-cols-2 gap-4;
}

.form-label {
  @apply block text-sm font-medium text-gray-700;
}

.form-input,
.form-select,
.form-textarea {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm;
}

.form-textarea {
  @apply resize-vertical;
}

.checkbox-container {
  @apply flex items-center gap-2 cursor-pointer text-sm;
}

.form-checkbox {
  @apply h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded;
}

.form-error {
  @apply text-sm text-red-600 font-medium p-3 bg-red-50 border border-red-200 rounded-lg;
}

.form-actions {
  @apply flex justify-end gap-3 pt-4 border-t border-gray-200;
}

.save-button {
  @apply flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-medium text-sm;
}

/* Confirmation Modal Styles */
.confirmation-modal {
  @apply max-w-md;
}

.modal-body {
  @apply p-6;
}

.confirmation-content {
  @apply text-center mb-6;
}

.confirmation-icon {
  @apply text-4xl mb-4;
}

.confirmation-message {
  @apply text-gray-700 text-sm leading-relaxed;
}

.confirmation-actions {
  @apply flex justify-center gap-3;
}

.cancel-button {
  @apply px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium;
}

.confirm-button {
  @apply px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium;
}

.confirm-button-success {
  @apply px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium;
}

.confirm-button-danger {
  @apply px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium;
}

/* Host Group Dropdown Styles */
.host-group-dropdown-wrapper {
  @apply relative;
}

.host-group-dropdown-trigger {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white flex items-center justify-between cursor-pointer;
}

.host-group-dropdown-trigger.dropdown-open {
  @apply border-blue-500 ring-2 ring-blue-500;
}

.dropdown-trigger-text {
  @apply text-gray-700 flex-1 text-left;
}

.dropdown-arrow {
  @apply text-gray-400 text-xs transition-transform duration-200;
}

.dropdown-arrow.arrow-up {
  @apply transform rotate-180;
}

.host-group-dropdown {
  @apply absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden;
}

.dropdown-search {
  @apply p-2 border-b border-gray-200;
}

.dropdown-search-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm;
}

.dropdown-select-all {
  @apply p-2 border-b border-gray-200 bg-gray-50;
}

.select-all-button {
  @apply flex items-center gap-2 w-full px-2 py-1.5 hover:bg-gray-100 rounded transition-colors cursor-pointer;
}

.select-all-checkbox {
  @apply h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer;
}

.select-all-text {
  @apply text-sm font-medium text-gray-700;
}

.dropdown-options {
  @apply max-h-48 overflow-y-auto;
}

.dropdown-option {
  @apply flex items-start gap-2 p-3 hover:bg-gray-50 cursor-pointer transition-colors;
}

.dropdown-option.option-selected {
  @apply bg-blue-50;
}

.option-checkbox {
  @apply mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer;
}

.option-content {
  @apply flex-1 flex flex-col gap-1;
}

.option-name {
  @apply text-sm font-medium text-gray-700;
}

.option-description {
  @apply text-xs text-gray-500;
}

.dropdown-no-results {
  @apply p-4 text-sm text-gray-500 text-center;
}

.selected-groups-chips {
  @apply flex flex-wrap gap-2 mt-2;
}

.group-chip {
  @apply inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium;
}

.chip-remove {
  @apply text-blue-700 hover:text-blue-900 font-bold cursor-pointer leading-none;
}
</style>
